<template>
  <div class="markdown">
    <slot v-if="loading" name="loading">
      <p>
        <v-skeleton-loader type="text" color="transparent" />
      </p>
    </slot>
    <!-- eslint-disable vue/no-v-html -->
    <div
      ref="container"
      @click.ctrl="click($event, true)"
      @click.exact="click($event)"
      v-html="html"
    />
    <font-awesome-icon
      ref="externalLinkIcon"
      :icon="faExternalLink"
      class="ml-1 markdown__external-link-icon"
    />
  </div>
</template>

<script lang="ts" setup>
import 'katex/dist/katex.min.css';
import 'markdown-it-texmath/css/texmath.css';
import type { Token } from 'markdown-it';
import type MarkdownItType from 'markdown-it';
import { nextTick, ref, toRefs, watch } from 'vue';
import { faExternalLink } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import router, { route } from '@/plugins/router';
import { RouteName } from '@/router/route-names';
import isExternalLink from '@/common/is-external-link';

// see: https://markdown-it.github.io/markdown-it/
type MarkdownOptions = {
  html?: boolean;
  breaks?: boolean;
  linkify?: boolean;
  quotes?: string | string[];
};

const { host, protocol } = document.location;
const absolutePath = `${protocol}//${host}`;
const rewriteLinks = (tokens: Token[]) => {
  tokens.forEach(token => {
    if (token.children) {
      rewriteLinks(token.children);
    }
    if (token.type !== 'link_open') {
      return;
    }
    if (!token.attrs) {
      return;
    }
    let [, href] = token.attrs.find(([key]: string[]) => key === 'href') || [];
    if (!href) {
      return;
    }
    if (href.startsWith(absolutePath)) {
      href = href.substring(absolutePath.length);
      token.attrSet('href', href);
      return;
    }
    if (href.startsWith('/downloads/')) {
      token.attrSet('target', '_blank');
      return;
    }
    if (isExternalLink(href)) {
      // Only set to blank if no target is set yet
      // For example by using markdown-it-attrs config: [all of this](/link){target="myCustomTarget"}
      if (!token.attrGet('target')) {
        token.attrSet('target', '_blank');
      }
    }
  });
};

type Props = {
  options?: MarkdownOptions;
  content?: string;
};
const props = withDefaults(defineProps<Props>(), {
  options: () => ({ html: false, breaks: true, linkify: false }),
});

const { content: markdownProp } = toRefs(props);
const html = ref('');
const loading = ref(false);

const renderMarkdown = async (markdown: string): Promise<string> => {
  if (!markdown) {
    return '';
  }
  loading.value = true;

  const [{ default: MarkdownIt }, { default: markdownItAttrs }] =
    await Promise.all([
      import('markdown-it'),
      import('markdown-it-attrs'), // Import the attrs plugin
    ]);
  const markdownIt = new MarkdownIt(props.options);
  // 1. Use the markdown-it-attrs plugin
  // This allows Markdown syntax like [text](url){target="_self"}
  markdownIt.use(markdownItAttrs);

  if (markdown.includes('$formulaStart{')) {
    const [markdownItTexmath, katex] = await Promise.all([
      import('markdown-it-texmath').then(module => module.default),
      import('katex').then(module => module.default),
    ]);
    markdownItTexmath.rules.zvdz = {
      inline: [
        {
          name: 'math_inline',
          rex: /\$formulaStart{(.+?)}formulaEnd/gy,
          tmpl: '<eq>$1</eq>',
          tag: '$formulaStart{',
        },
      ],
      block: [],
    };

    markdownIt.use(markdownItTexmath, {
      engine: katex,
      delimiters: 'zvdz',
    });
  }

  markdownIt.use((md: MarkdownItType) => {
    md.core.ruler.push('ZvdZ-link-target', ({ tokens }) => {
      rewriteLinks(tokens);
      return false;
    });
  });

  loading.value = false;
  return markdownIt.render(markdown);
};

const container = ref<HTMLElement>();
const externalLinkIcon = ref<typeof FontAwesomeIcon>();
const addExternalLinkIcons = () => {
  if (!container.value || !externalLinkIcon.value?.$el) {
    return;
  }

  container.value?.querySelectorAll('a[target="_blank"]').forEach(el => {
    const icon = externalLinkIcon.value?.$el.cloneNode(true);
    if (!icon) {
      return;
    }
    el.appendChild(icon);
  });
};

if (markdownProp?.value) {
  watch(
    markdownProp,
    async m => {
      if (!m) {
        return;
      }
      html.value = await renderMarkdown(m);

      await nextTick(addExternalLinkIcons);
    },
    { immediate: true },
  );
}

const click = (event: Event, ctrl?: boolean) => {
  if (!(event.target instanceof HTMLElement) || event.target.tagName !== 'A') {
    return;
  }
  const a = event.target;
  const href = a.getAttribute('href');
  if (!href) {
    return;
  }
  if (!isExternalLink(href) && !ctrl) {
    const resolvedRoute = router.resolve(href, route.value);
    if (resolvedRoute && resolvedRoute?.name !== RouteName.CatchAll) {
      event.preventDefault();
      router.push(href);
    }
  }
};
</script>

<style lang="scss">
.markdown {
  h1,
  h2,
  h3,
  h4,
  h5,
  h6,
  ol,
  ul {
    margin-bottom: 1rem;
  }

  ol,
  ul {
    // Show list numbering / bullets inside the container, not outside.
    list-style-position: inside;
  }

  table,
  th,
  td {
    border: 1px solid;
  }

  table {
    border-collapse: collapse;
  }

  th {
    text-align: start;
  }

  th,
  td {
    padding: 5px 10px;
  }

  > .markdown__external-link-icon {
    display: none;
  }
}
</style>
